import React, { useEffect, useState } from "react"
import { useFormik } from "formik"
import * as Yup from "yup"
import styled from "@emotion/styled"
import { Flex, Text } from "@theme-ui/components"
import { Link, navigate } from "gatsby"
import { useAccountContext } from "../../../context/AccountContext"
import { useInterfaceContext } from "../../../context/InterfaceContext"
import { handleUrlPrefix } from "../../../utils/translations-util"
import Image from "../../new-ui/image"
import Layout from "../../layouts"
import InputField from "../../new-ui/input-field"
import Button from "../../new-ui/button"
import { useNotificationContext } from "../../../context/NotificationContext"
import useDrawerHook from "../../../hooks/useDrawerHook"
import { trackUserSignedIn } from "../../../services/analytics"

import { uniqBy } from "lodash"

import Medusa from "../../../services/api"

const Container = styled(Flex)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  flex: 1;
`

const ImageContainer = styled(Flex)`
  flex: 1;
  flex-basis: 100%;
  ${(props) => props.theme.breakpointsLegacy.desktop} {
    flex-basis: 55%;
  }
`

const LoginContainer = styled(Flex)`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  justify-content: center;
  align-items: center;
  flex: 1;
  flex-basis: 100%;
  flex-direction: column;
  margin: 0 20px;
  ${(props) => props.theme.breakpointsLegacy.desktop} {
    flex-basis: 45%;
    position: initial;
    background-color: ${(props) => props.theme.colors.grayscale[200]};
    margin: 0px;
  }
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;

  margin-bottom: 0px;

  > div,
  button {
    margin-bottom: 8px;
  }
  ${(props) => props.theme.breakpointsLegacy.desktop} {
    max-width: 334px;
  }
`

const SignIn = ({ data = {}, hideImage = false }) => {
  const { email } = useAccountContext()

  useEffect(() => {
    // if the user is already logged in, navigate to welcom back page
    if (email) {
      navigate("/account/welcome-back", { replace: true })
    }
  }, [email])

  return (
    <Layout hideFooter={true} isLoginPage={true}>
      <Container>
        {!hideImage && (
          <ImageContainer>
            {data && (
              <Image image={data.tile.backgroundImage.gatsbyImageData} />
            )}
          </ImageContainer>
        )}
        <LoginContainer>
          <Text
            as="h1"
            sx={{
              textAlign: "center",
              fontSize: "27px",
              color: "primary",
              mb: "2em",
              fontWeight: "normal",
            }}
          >
            Welcome back! Please log in here
          </Text>
          <LoginForm showSignup={false} />

          <Flex
            sx={{
              flexDirection: "column",
              textAlign: "center",
              marginBlockStart: 8,
              a: {
                color: "primary",
              },
            }}
          >
            <Text>New to Tekla?</Text>
            <Link to={"/account/sign-up"}>Sign up</Link>
          </Flex>
        </LoginContainer>
      </Container>
    </Layout>
  )
}

export default SignIn

export const LoginForm = ({ afterSubmit, showSignup = false }) => {
  const ValidationSchema = Yup.object().shape({
    email: Yup.string().email().required("Required"),
    password: Yup.string().required("Required"),
  })

  const { setWishlistCount } = useInterfaceContext()

  const { pushNotification } = useNotificationContext()
  const { logIn, forgotPassword, setUser } = useAccountContext()
  const { drawerContent, drawerVisible, setDrawerContent, setDrawerVisible } =
    useDrawerHook({
      config: {
        asModal: true,
        sx: { ".content": { backgroundColor: "grayscale.white" } },
      },
    })

  useEffect(() => {
    setDrawerContent(
      <ForgotPasswordContent
        forgotPassword={forgotPassword}
        pushNotification={pushNotification}
      />
    )
  }, [])

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validateOnChange: false,
    onSubmit: async (values) => {
      const { email, password } = values

      await logIn(email, password)
        .then(async (data) => {
          let updated = {}

          if (data?.customer?.id) {
            let wishlist = JSON.parse(
              localStorage.getItem("medusa::cache::wishlist")
            )

            wishlist = data.metadata?.wishlist?.length
              ? wishlist.filter(
                  (item) =>
                    !data.metadata.wishlist
                      .map((i) => i.variant_id)
                      .includes(item.variantId)
                )
              : wishlist

            if (wishlist && wishlist.length) {
              for (const item of wishlist) {
                const res = await Medusa.customers.addToWishList(
                  data.customer.id,
                  {
                    variant_id: item.variantId,
                    quantity: 1,
                  }
                )

                if (res?.data) {
                  updated = res.data
                }
              }
            }
          }

          if (updated?.customer) {
            setUser(updated.customer)
          }

          if (afterSubmit) {
            afterSubmit(data)
          }

          let parsed = uniqBy(
            updated?.customer
              ? updated.customer.metadata?.wishlist
              : data?.customer?.metadata?.wishlist,
            "variant_id"
          )

          setWishlistCount(parsed?.length)

          trackUserSignedIn(data?.customer)

          pushNotification({
            id: "sign-in",
            body: "Success",
            dismiss: { duration: 3000 },
          })
        })
        .catch(() =>
          pushNotification({
            id: "sign-in",
            body: "Sorry, something went wrong. Make sure the input is correct and try again",
            dismiss: { duration: 3000 },
          })
        )
    },
    validationSchema: ValidationSchema,
  })

  const handleSignupLink = () => {
    return "/account/sign-up"
  }

  return (
    <>
      {drawerVisible && drawerContent}
      <Form onSubmit={formik.handleSubmit}>
        <InputField
          id="email"
          name="email"
          type="text"
          placeholder={"E-mail"}
          value={formik.values.email}
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          error={formik.touched.email && formik.errors.email}
        />
        <InputField
          id="password"
          name="password"
          type="password"
          placeholder={"Password"}
          value={formik.values.password}
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          error={formik.touched.password && formik.errors.password}
        />
        <Text
          onClick={() => setDrawerVisible(true)}
          sx={{ cursor: "pointer", py: "1em", textDecoration: "underline" }}
        >
          I forgot my password
        </Text>
        <Button type="submit">Sign in</Button>

        {showSignup && (
          <Flex
            sx={{
              flexDirection: "column",
              textAlign: "center",
              a: {
                color: "primary",
              },
            }}
          >
            <Text>New to Tekla?</Text>
            <Link to={handleSignupLink()}>Sign up</Link>
          </Flex>
        )}
      </Form>
    </>
  )
}

const ForgotPasswordContent = ({ onComplete }) => {
  const { forgotPassword } = useAccountContext()
  const { pushNotification } = useNotificationContext()
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("")

  return (
    <Flex
      sx={{
        maxWidth: "375px",
        p: 1,
        flexDirection: "column",
        bg: "grayscale.white",
      }}
    >
      <Text
        as="h2"
        sx={{
          fontSize: "21px",
          mt: "20px",
          mb: "8px",
          paddingRight: "20%",
          fontWeight: "normal",
        }}
      >
        I forgot my password
      </Text>
      <Text
        as="p"
        sx={{
          fontSize: "13px",
          marginBottom: "12px",
          paddingRight: "20%",
          lineHeight: "17px",
        }}
      >
        Please enter your email below and we will send you a link to create a
        new password
      </Text>
      <InputField
        sx={{ mb: "8px", mt: "16px" }}
        value={forgotPasswordEmail}
        placeholder={"Enter your email"}
        onChange={(e) => {
          setForgotPasswordEmail(e.target.value)
        }}
      />
      <Button
        sx={{ fontWeight: "bold", fontSize: "14px" }}
        onClick={async () => {
          forgotPassword(forgotPasswordEmail)
            .then(() => {
              pushNotification({
                id: "forgot_password",
                body: "Success",
                dismiss: { duration: 3000 },
              })
              if (onComplete) {
                onComplete()
              }
            })
            .catch(() => {
              pushNotification({
                id: "forgot_password",
                body: "Email doesn't exist",
                dismiss: { duration: 3000 },
              })
            })
        }}
      >
        Send
      </Button>
    </Flex>
  )
}
